import { useState, useEffect, useMemo } from 'react'
import { apiClient, WeeklyShiftRequest, User } from '../lib/api'
import { useLiff } from '../hooks/useLiff'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorAlert from '../components/ErrorAlert'

const DAY_LABELS = ['水曜日', '木曜日', '金曜日', '土曜日', '日曜日', '月曜日']

interface AdminRequest extends WeeklyShiftRequest {
  display_name: string
}

interface ApprovalState {
  [id: number]: 'approved' | 'rejected' | 'requested'
}

function getNextWednesday(): Date {
  const now = new Date()
  const day = now.getDay()
  let daysUntilWed = (3 - day + 7) % 7
  if (daysUntilWed === 0) daysUntilWed = 0
  const wed = new Date(now)
  wed.setDate(now.getDate() + daysUntilWed)
  return wed
}

function getWeekDates(wednesday: Date): string[] {
  const dates: string[] = []
  for (let i = 0; i < 6; i++) {
    const d = new Date(wednesday)
    d.setDate(wednesday.getDate() + i)
    dates.push(d.toISOString().split('T')[0])
  }
  return dates
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0]
}

export default function ShiftAdmin() {
  const { accessToken, isReady } = useLiff()
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [requests, setRequests] = useState<AdminRequest[]>([])
  const [approvalStates, setApprovalStates] = useState<ApprovalState>({})
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [weekOffset, setWeekOffset] = useState(0)

  const wednesday = useMemo(() => {
    const w = getNextWednesday()
    w.setDate(w.getDate() + weekOffset * 7)
    return w
  }, [weekOffset])

  const weekDates = useMemo(() => getWeekDates(wednesday), [wednesday])
  const weekStart = formatDate(wednesday)

  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadUser()
    }
  }, [isReady, accessToken])

  const loadUser = async () => {
    try {
      const response = await apiClient.getMe()
      setUser(response.user)
    } catch (e) {
      console.error('Failed to load user:', e)
    } finally {
      setUserLoading(false)
    }
  }

  useEffect(() => {
    if (user && user.role !== 'admin') {
      alert('管理者権限が必要です')
      window.history.back()
    }
  }, [user])

  useEffect(() => {
    if (!user || userLoading) return
    fetchRequests()
  }, [user, userLoading, weekStart])

  const fetchRequests = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await apiClient.getAllWeeklyShifts(weekStart)
      setRequests(data.requests as AdminRequest[])
      const states: ApprovalState = {}
      for (const r of data.requests) {
        states[r.id] = r.status as 'approved' | 'rejected' | 'requested'
      }
      setApprovalStates(states)
    } catch (e) {
      console.error('Failed to fetch:', e)
      setError(e instanceof Error ? e.message : 'データの取得に失敗しました')
    }
    setLoading(false)
  }

  const handleApprovalChange = (id: number, status: 'approved' | 'rejected') => {
    setApprovalStates(prev => ({ ...prev, [id]: status }))
  }

  const handleApproveAll = async () => {
    if (submitting) return

    const changes = requests
      .filter(r => approvalStates[r.id] !== r.status)
      .map(r => ({ requestId: r.id, status: approvalStates[r.id] as 'approved' | 'rejected' }))

    if (changes.length === 0) {
      alert('変更がありません')
      return
    }

    setSubmitting(true)
    try {
      const result = await apiClient.approveWeeklyShifts(changes)
      alert(`シフトを確定しました。${result.notificationsSent}人に通知を送信しました。`)
      await fetchRequests()
    } catch (e) {
      console.error('Failed to approve:', e)
      alert('確定に失敗しました')
    }
    setSubmitting(false)
  }

  // 一括承認ボタン
  const handleBulkApprove = () => {
    const newStates = { ...approvalStates }
    for (const r of requests) {
      if (r.status === 'requested') {
        newStates[r.id] = 'approved'
      }
    }
    setApprovalStates(newStates)
  }

  if (!isReady || userLoading) {
    return <LoadingSpinner message="読み込み中..." />
  }

  const hasChanges = requests.some(r => approvalStates[r.id] !== r.status)

  // Group by date
  const requestsByDate = new Map<string, AdminRequest[]>()
  for (const date of weekDates) {
    requestsByDate.set(date, requests.filter(r => r.date === date))
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-gray-900">📅 シフト管理</h1>
            <p className="text-sm text-gray-600 mt-1">
              {wednesday.getMonth() + 1}月{wednesday.getDate()}日（水）〜
            </p>
          </div>

          {/* 週移動 */}
          <div className="flex justify-between items-center mb-4">
            <button onClick={() => setWeekOffset(o => o - 1)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">← 前週</button>
            <button onClick={() => setWeekOffset(0)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">今週</button>
            <button onClick={() => setWeekOffset(o => o + 1)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">翌週 →</button>
          </div>

          <ErrorAlert error={error} onClose={() => setError('')} />

          <div className="flex gap-2 mb-6">
            <button
              onClick={handleBulkApprove}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              全て承認
            </button>
            <button
              onClick={handleApproveAll}
              disabled={submitting || !hasChanges}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            >
              {submitting ? '確定中...' : '確定して通知'}
            </button>
          </div>

          {loading ? (
            <LoadingSpinner message="読み込み中..." />
          ) : (
            <div className="space-y-6">
              {weekDates.map((date, index) => {
                const dayRequests = requestsByDate.get(date) || []
                const d = new Date(date + 'T00:00:00')

                return (
                  <div key={date} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-lg text-gray-900 mb-3">
                      {DAY_LABELS[index]} ({d.getMonth() + 1}/{d.getDate()})
                    </h3>

                    {dayRequests.length > 0 ? (
                      <div className="space-y-2">
                        {dayRequests.map(r => (
                          <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{r.display_name}</div>
                              <div className="text-sm text-gray-600">
                                {r.start_time} 〜 {r.end_time}
                              </div>
                              {r.note && <div className="text-xs text-gray-500 mt-1">📝 {r.note}</div>}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprovalChange(r.id, 'approved')}
                                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                  approvalStates[r.id] === 'approved'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                                }`}
                              >
                                承認
                              </button>
                              <button
                                onClick={() => handleApprovalChange(r.id, 'rejected')}
                                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                  approvalStates[r.id] === 'rejected'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                                }`}
                              >
                                却下
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 py-4">希望者なし</div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {hasChanges && (
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                ⚠️ 変更があります。「確定して通知」ボタンで確定してください。
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
